import { useEffect, useState } from 'react'
import { Alert, Box, Button, Stack, TextField, Typography } from '@mui/material'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { SchemaSmtp, caricaSmtp, esitoInvio, provaSmtp, salvaSmtp, type DatiSmtp } from './smtp.api'

const VUOTO: DatiSmtp = {
  host: '',
  porta: '587',
  sicurezza: 'starttls',
  utente: '',
  password: '',
  mittenteNome: '',
  mittenteEmail: '',
}

/** Account SMTP del partner (MAIL-01) e prova di invio (MAIL-02): le email ai
 * clienti partono da qui con la sua identita'; senza, da Resend. */
export function AccountInvio() {
  const queryClient = useQueryClient()
  const { data: config, isLoading } = useQuery({ queryKey: ['smtp'], queryFn: caricaSmtp })
  const [dati, setDati] = useState<DatiSmtp>(VUOTO)
  const [destinatario, setDestinatario] = useState('')
  const [errore, setErrore] = useState<string | null>(null)
  const [salvato, setSalvato] = useState(false)
  const [prova, setProva] = useState<number | null>(null)

  useEffect(() => {
    if (!config) return
    setDati({ ...config, porta: String(config.porta), password: '' })
    setDestinatario(config.mittenteEmail)
  }, [config])

  // Il worker spedisce entro pochi secondi: si ricontrolla finche' l'esito
  // non e' scritto nel registro.
  const { data: esito } = useQuery({
    queryKey: ['esito-prova', prova],
    queryFn: () => esitoInvio(prova!),
    enabled: prova !== null,
    refetchInterval: (q) => (q.state.data?.stato === 'in_coda' ? 2000 : false),
  })

  const imposta = (campo: keyof DatiSmtp) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setSalvato(false)
    setDati((d) => ({ ...d, [campo]: e.target.value }))
  }

  async function suSalva() {
    const verifica = SchemaSmtp.safeParse(dati)
    if (!verifica.success) {
      setErrore(verifica.error.issues[0]!.message)
      return
    }
    setErrore(null)
    try {
      await salvaSmtp(dati)
      setSalvato(true)
      await queryClient.invalidateQueries({ queryKey: ['smtp'] })
    } catch (e) {
      setErrore(e instanceof Error ? e.message : 'errore imprevisto')
    }
  }

  async function suProva() {
    setErrore(null)
    try {
      setProva(await provaSmtp(destinatario))
    } catch (e) {
      setErrore(e instanceof Error ? e.message : 'errore imprevisto')
    }
  }

  if (isLoading) return null

  return (
    <Box sx={{ mb: 4 }}>
      <Typography variant="h6" sx={{ mb: 1 }}>
        Account di invio
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Le email ai clienti partono da questo account, con il nome del tuo studio. Finche non lo configuri partono
        dal dominio della piattaforma.
      </Typography>
      <Alert severity="info" role="note" sx={{ mb: 2 }}>
        L account di posta deve supportare l invio massivo: la piattaforma non risponde dei mancati invii che
        dipendono dal provider.
      </Alert>
      {errore !== null && (
        <Alert severity="error" role="alert" sx={{ mb: 2 }}>
          {errore}
        </Alert>
      )}
      {salvato && <Alert severity="success" role="status" sx={{ mb: 2 }}>Account salvato</Alert>}

      <Stack spacing={2} sx={{ maxWidth: 560 }}>
        <Stack direction="row" spacing={2}>
          <TextField size="small" label="Server in uscita" fullWidth value={dati.host} onChange={imposta('host')} />
          <TextField size="small" label="Porta" type="number" sx={{ width: 120 }} value={dati.porta} onChange={imposta('porta')} />
          <TextField
            select
            size="small"
            label="Sicurezza"
            sx={{ width: 160 }}
            slotProps={{ select: { native: true }, inputLabel: { shrink: true } }}
            value={dati.sicurezza}
            onChange={imposta('sicurezza')}
          >
            <option value="starttls">STARTTLS</option>
            <option value="ssl">SSL/TLS</option>
          </TextField>
        </Stack>
        <TextField size="small" label="Nome utente" value={dati.utente} onChange={imposta('utente')} />
        <TextField
          size="small"
          label="Password"
          type="password"
          autoComplete="new-password"
          helperText={config ? 'Lascia vuoto per non cambiarla' : undefined}
          value={dati.password}
          onChange={imposta('password')}
        />
        <Stack direction="row" spacing={2}>
          <TextField size="small" label="Nome del mittente" fullWidth value={dati.mittenteNome} onChange={imposta('mittenteNome')} />
          <TextField size="small" label="Email del mittente" fullWidth value={dati.mittenteEmail} onChange={imposta('mittenteEmail')} />
        </Stack>
        <Box>
          <Button variant="contained" onClick={() => void suSalva()}>
            Salva account
          </Button>
        </Box>

        <Stack direction="row" spacing={2} alignItems="center">
          <TextField
            size="small"
            label="Invia la prova a"
            fullWidth
            value={destinatario}
            onChange={(e) => setDestinatario(e.target.value)}
          />
          <Button
            variant="outlined"
            sx={{ whiteSpace: 'nowrap' }}
            disabled={!config || esito?.stato === 'in_coda'}
            onClick={() => void suProva()}
          >
            Invia email di prova
          </Button>
        </Stack>
        {prova !== null && (!esito || esito.stato === 'in_coda') && <Typography>Prova in corso…</Typography>}
        {esito?.stato === 'inviata' && (
          <Alert severity="success" role="status">Email di prova inviata: controlla la casella di {destinatario}.</Alert>
        )}
        {esito?.stato === 'errore' && <Alert severity="error">{esito.errore}</Alert>}
      </Stack>
    </Box>
  )
}
